import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { categories } from "@/mocks/categories";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-white/95 backdrop-blur-md border-b border-background-200 shadow-sm`}
    >
      <div className="container-wide flex items-center justify-between h-16 md:h-20">
        <Link to="/" className="flex items-center gap-2 shrink-0">
          <div className="w-8 h-8 md:w-10 md:h-10 bg-primary-500 rounded-lg flex items-center justify-center">
            <i className="ri-file-list-3-line text-white text-lg md:text-xl" />
          </div>
          <span className="font-heading font-semibold text-base md:text-lg tracking-tight text-foreground-900">
            Kakobuy Spreadsheet
          </span>
        </Link>

        <div className="hidden md:flex items-center gap-6">
          <Link
            to="/"
            className={`text-sm font-medium transition-colors hover:text-primary-500 ${
              isHome ? "text-primary-600" : "text-foreground-700"
            }`}
          >
            <span className="flex items-center gap-1.5">
              <i className="ri-home-4-line" />
              Home
            </span>
          </Link>

          <div className="relative">
            <button
              onMouseEnter={() => setDropdownOpen(true)}
              className={`flex items-center gap-1 text-sm font-medium transition-colors hover:text-primary-500 cursor-pointer text-foreground-700`}
            >
              <i className="ri-grid-line" />
              Categories
              <i className="ri-arrow-down-s-line text-sm" />
            </button>
            {dropdownOpen && (
              <div
                onMouseLeave={() => setDropdownOpen(false)}
                className="absolute top-full left-0 mt-2 w-64 bg-foreground-800 rounded-xl shadow-lg overflow-hidden py-2"
              >
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-3 px-4 py-2.5 text-sm text-white/90 hover:text-white hover:bg-foreground-700 transition-colors"
                  >
                    <img
                      src={cat.image}
                      alt={cat.name}
                      className="w-8 h-8 rounded-md object-cover object-top shrink-0"
                    />
                    <span>{cat.name}</span>
                  </a>
                ))}
              </div>
            )}
          </div>

          <Link
            to="/blog"
            className="text-sm font-medium transition-colors hover:text-primary-500 text-foreground-700"
          >
            <span className="flex items-center gap-1.5">
              <i className="ri-article-line" />
              Blog
            </span>
          </Link>

          <Link
            to="/blog/safe-shopping-kakobuy"
            className="text-sm font-medium transition-colors hover:text-primary-500 text-foreground-700"
          >
            <span className="flex items-center gap-1.5">
              <i className="ri-shopping-cart-line" />
              Shopping Guide
            </span>
          </Link>

          <Link
            to="/blog/best-kakobuy-spreadsheet"
            className="text-sm font-medium transition-colors hover:text-primary-500 text-foreground-700"
          >
            <span className="flex items-center gap-1.5">
              <i className="ri-book-open-line" />
              Guides
            </span>
          </Link>
        </div>

        <div className="hidden md:flex items-center gap-3">
          <a
            href="https://kaspreadsheet.com/"
            target="_blank"
            rel="noopener nofollow"
            className="btn-primary text-sm py-2 px-4"
          >
            Spreadsheet
          </a>
          <a
            href="https://kaspreadsheet.com/"
            target="_blank"
            rel="noopener nofollow"
            className="btn-accent text-sm py-2 px-4"
          >
            Buy It Now
          </a>
        </div>

        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg cursor-pointer text-foreground-900"
        >
          <i className="ri-menu-line text-xl" />
        </button>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-white border-t border-background-200 shadow-lg">
          <div className="px-4 py-4 space-y-1">
            <Link
              to="/"
              onClick={() => setMobileOpen(false)}
              className="flex items-center gap-2 py-3 text-sm font-medium text-foreground-700 hover:text-primary-500"
            >
              <i className="ri-home-4-line text-primary-500" />
              Home
            </Link>
            <Link
              to="/blog"
              onClick={() => setMobileOpen(false)}
              className="flex items-center gap-2 py-3 text-sm font-medium text-foreground-700 hover:text-primary-500"
            >
              <i className="ri-article-line text-primary-500" />
              Blog
            </Link>
            <Link
              to="/blog/safe-shopping-kakobuy"
              onClick={() => setMobileOpen(false)}
              className="flex items-center gap-2 py-3 text-sm font-medium text-foreground-700 hover:text-primary-500"
            >
              <i className="ri-shopping-cart-line text-primary-500" />
              Shopping Guide
            </Link>
            <Link
              to="/blog/best-kakobuy-spreadsheet"
              onClick={() => setMobileOpen(false)}
              className="flex items-center gap-2 py-3 text-sm font-medium text-foreground-700 hover:text-primary-500"
            >
              <i className="ri-book-open-line text-primary-500" />
              Guides
            </Link>
            <div className="py-2 border-t border-background-200 mt-2">
              <p className="text-xs font-medium text-foreground-400 uppercase tracking-wider mb-3">
                Categories
              </p>
              <div className="grid grid-cols-2 gap-2">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center gap-2 py-2 px-3 rounded-lg text-sm text-foreground-600 hover:text-primary-500 hover:bg-background-100"
                  >
                    <img
                      src={cat.image}
                      alt={cat.name}
                      className="w-8 h-8 rounded-md object-cover object-top shrink-0"
                    />
                    <span className="truncate">{cat.name}</span>
                  </a>
                ))}
              </div>
            </div>
            <div className="pt-4 flex flex-col gap-2 border-t border-background-200 mt-2">
              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                className="btn-primary text-center text-sm"
              >
                Kakobuy Spreadsheet
              </a>
              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                className="btn-accent text-center text-sm"
              >
                Buy It Now
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}